/**
 * Office chair generator — a task chair built from box and cylinder geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width, Y = depth (sitter faces +Y), Z = height
 *
 * Local anchor: origin at the geometric centre of the base footprint at Z = 0.
 *
 * The chair is a compound group so every structural part can be individually
 * selected and assigned a different material (fabric seat, plastic shell,
 * aluminium base).
 *
 * Parts:
 *   seat      — a box cushion at `seatHeight`.
 *   back      — a taller box panel, angled back by `backTilt`. Can optionally
 *               have a lumbar bulge (extra box at lower third of the back).
 *   armrests  — two post + pad assemblies outside the seat (optional).
 *   pole      — a vertical cylinder connecting the base to the seat.
 *   base      — a five-arm star base on the floor; each arm is a flat box
 *               radiating outward at 72° intervals, built using transformGeometry.
 *   casters   — five small box stubs at the outer end of each base arm.
 *
 * Back styles:
 *   mid_back  — moderate-height panel (default task-chair look)
 *   high_back — taller executive-chair panel reaching toward head height
 *   low_back  — short panel exposing the lower back
 *
 * The lumbar option adds a subtle protrusion at the lower third of the back
 * panel — one extra thin box box offset slightly forward of the panel face —
 * to represent lumbar support curvature without full mesh subdivision.
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import {
  buildBox,
  buildCylinder,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorLocalTransform,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface OfficeChairParams extends GeneratorParams {
  seatWidth: number;
  seatDepth: number;
  seatHeight: number;
  seatThickness: number;
  backStyle: string;
  backTilt: number;
  backThickness: number;
  hasLumbar: boolean;
  hasArmrests: boolean;
  armrestHeight: number;
  armrestDepth: number;
  baseRadius: number;
  poleHeight: number;
}

const OFFICE_CHAIR_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "oc-seat",
    label: "Seat",
    params: [
      {
        id: "seatWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.5,
        min: 0.35,
        max: 0.8,
        step: 0.01,
      },
      {
        id: "seatDepth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.48,
        min: 0.35,
        max: 0.7,
        step: 0.01,
      },
      {
        id: "seatHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.48,
        min: 0.35,
        max: 0.65,
        step: 0.01,
        description:
          "Floor-to-seat-top height (mid-range of a typical pneumatic range).",
      },
      {
        id: "seatThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.06,
        min: 0.03,
        max: 0.15,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "oc-back",
    label: "Back",
    params: [
      {
        id: "backStyle",
        label: "Style",
        type: "enum",
        default: "mid_back",
        options: [
          { value: "low_back", label: "Low Back" },
          { value: "mid_back", label: "Mid Back" },
          { value: "high_back", label: "High Back" },
        ],
      },
      {
        id: "backTilt",
        label: "Tilt Angle",
        type: "number",
        unit: "degrees",
        default: 10,
        min: 0,
        max: 30,
        step: 1,
      },
      {
        id: "backThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.06,
        min: 0.03,
        max: 0.15,
        step: 0.005,
      },
      {
        id: "hasLumbar",
        label: "Lumbar Support",
        type: "boolean",
        default: true,
      },
    ],
  },
  {
    type: "section",
    id: "oc-armrests",
    label: "Armrests",
    params: [
      { id: "hasArmrests", label: "Armrests", type: "boolean", default: true },
      {
        id: "armrestHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.1,
        max: 0.45,
        step: 0.01,
        description: "Armrest pad height above the seat surface.",
        showIf: { id: "hasArmrests", value: true },
      },
      {
        id: "armrestDepth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.1,
        max: 0.4,
        step: 0.01,
        showIf: { id: "hasArmrests", value: true },
      },
    ],
  },
  {
    type: "section",
    id: "oc-base",
    label: "Base",
    defaultCollapsed: true,
    params: [
      {
        id: "baseRadius",
        label: "Base Radius",
        type: "number",
        unit: "meters",
        default: 0.33,
        min: 0.2,
        max: 0.55,
        step: 0.01,
      },
      {
        id: "poleHeight",
        label: "Pole Height",
        type: "number",
        unit: "meters",
        default: 0.36,
        min: 0.1,
        max: 0.55,
        step: 0.01,
        description: "Height of the gas-lift pole beneath the seat.",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Back height by style
// ---------------------------------------------------------------------------

const BACK_HEIGHTS: Record<string, number> = {
  low_back: 0.35,
  mid_back: 0.52,
  high_back: 0.72,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lt(pos: [number, number, number]): GeneratorLocalTransform {
  return { position: pos, rotation: [0, 0, 0], scale: [1, 1, 1] };
}

/**
 * Build the 5-arm star base geometry.
 *
 * Layout (Z-up):
 *   hub      — central cylinder, Z = 0 → hubH.  The pole sits on top of it.
 *   arms     — five flat boxes raised armZ off the floor, so they hover
 *              visibly above the ground plane (realistic for a task-chair base
 *              where caster wheels lift the frame).
 *   casters  — short square stubs at each arm tip, from Z = 0 to Z = armZ,
 *              bridging the gap between the arm underside and the floor.
 */
function buildStarBase(baseRadius: number): PrimitiveGeometry {
  const armLen = baseRadius;
  const armW = 0.048;
  const armH = 0.06; // arm thickness (was 0.04 — too flat)
  const armZ = 0.03; // arm bottom sits 3 cm above floor
  const hubR = 0.058;
  const hubH = 0.06; // must match baseTopZ constant in buildOfficeChair
  const casterW = armW * 1.3;
  const casterH = armZ; // fills the gap between arm bottom and floor

  const parts: PrimitiveGeometry[] = [];

  // Central hub — from floor to hubH so the pole has a solid base
  parts.push(transformGeometry(buildCylinder(hubR, hubH), [0, 0, hubH * 0.5]));

  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * 2 * Math.PI;
    const cx = Math.cos(angle) * armLen * 0.5;
    const cy = Math.sin(angle) * armLen * 0.5;

    // Arm: raised armZ off the floor, centred at armZ + armH/2
    parts.push(
      transformGeometry(
        buildBox(armW, armLen, armH),
        [cx, cy, armZ + armH * 0.5],
        [0, 0, angle],
      ),
    );

    // Caster: short stub at the arm tip, sitting on the floor
    const tipX = Math.cos(angle) * (armLen - casterW * 0.5);
    const tipY = Math.sin(angle) * (armLen - casterW * 0.5);
    parts.push(
      transformGeometry(buildBox(casterW, casterW, casterH), [
        tipX,
        tipY,
        casterH * 0.5,
      ]),
    );
  }

  return concatPrimitiveGeometries(parts);
}

/**
 * Build the pneumatic pole: cylinder from top of base hub to underside of seat.
 */
function buildPole(poleHeight: number): PrimitiveGeometry {
  const poleR = 0.035;
  return buildCylinder(poleR, poleHeight);
}

/**
 * Build the back panel in child-local space.
 * Origin at hinge (Z = 0). Geometry extends Z = 0 to Z = backHeight.
 * Optional lumbar box protrudes slightly forward at the lower third.
 */
function buildBackPanel(
  seatWidth: number,
  backHeight: number,
  backThickness: number,
  hasLumbar: boolean,
): PrimitiveGeometry {
  const panelGeo = transformGeometry(
    buildBox(seatWidth * 0.9, backThickness, backHeight),
    [0, 0, backHeight * 0.5],
  );

  if (!hasLumbar) return panelGeo;

  // Lumbar protrusion: a thin box at lower-third height, offset slightly
  // toward the sitter (+Y in child space, since back faces −Y of seat).
  const lumbarH = backHeight * 0.25;
  const lumbarCZ = backHeight * 0.28;
  const lumbarT = backThickness * 0.35;
  const lumbarGeo = transformGeometry(
    buildBox(seatWidth * 0.75, lumbarT, lumbarH),
    [0, backThickness * 0.5 + lumbarT * 0.5, lumbarCZ],
  );

  return concatPrimitiveGeometries([panelGeo, lumbarGeo]);
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildOfficeChair(
  p: OfficeChairParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const seatW = numParam(p, "seatWidth", 0.5);
  const seatD = numParam(p, "seatDepth", 0.48);
  const seatH = numParam(p, "seatHeight", 0.48);
  const seatT = numParam(p, "seatThickness", 0.06);
  const backSty = enumParam(p, "backStyle", "mid_back");
  const backTilt = numParam(p, "backTilt", 10) * (Math.PI / 180);
  const backT = numParam(p, "backThickness", 0.06);
  const hasLumbar = boolParam(p, "hasLumbar", true);
  const hasArms = boolParam(p, "hasArmrests", true);
  const armH = numParam(p, "armrestHeight", 0.22);
  const armD = numParam(p, "armrestDepth", 0.22);
  const baseR = numParam(p, "baseRadius", 0.33);
  const poleH = numParam(p, "poleHeight", 0.36);

  const backHeight = BACK_HEIGHTS[backSty] ?? 0.52;

  // Clamp pole height so it doesn't exceed seat height
  const clampedPoleH = Math.min(poleH, seatH - seatT - 0.06);

  const children: GeneratorChild[] = [];

  // ── Star base ─────────────────────────────────────────────────────────────
  children.push({
    roleId: "base",
    name: "Base",
    objectKind: "imported",
    geometry: buildStarBase(baseR),
    localTransform: lt([0, 0, 0]),
  });

  // ── Pneumatic pole ────────────────────────────────────────────────────────
  const baseTopZ = 0.06; // hub height from buildStarBase
  const poleCentZ = baseTopZ + clampedPoleH * 0.5;
  children.push({
    roleId: "pole",
    name: "Pole",
    objectKind: "imported",
    geometry: buildPole(clampedPoleH),
    localTransform: lt([0, 0, poleCentZ]),
  });

  // ── Seat ──────────────────────────────────────────────────────────────────
  children.push({
    roleId: "seat",
    name: "Seat",
    objectKind: "cube",
    geometry: buildBox(seatW, seatD, seatT),
    localTransform: lt([0, 0, seatH - seatT * 0.5]),
  });

  // ── Back ──────────────────────────────────────────────────────────────────
  // Hinge at the rear of the seat top (same convention as chair / armchair).
  const backGeo = buildBackPanel(seatW, backHeight, backT, hasLumbar);
  children.push({
    roleId: "back",
    name: "Back",
    objectKind: "cube",
    geometry: backGeo,
    localTransform: {
      position: [0, -(seatD * 0.5 + backT * 0.5), seatH],
      rotation: [backTilt, 0, 0],
      scale: [1, 1, 1],
    },
  });

  // ── Armrests ──────────────────────────────────────────────────────────────
  if (hasArms) {
    const postT = 0.025;
    const padT = 0.03;
    const postH = armH - padT;
    const armGeo = concatPrimitiveGeometries([
      // Vertical support post
      transformGeometry(buildBox(postT, postT, postH), [0, 0, postH * 0.5]),
      // Horizontal pad on top of post
      transformGeometry(buildBox(postT * 2, armD, padT), [
        0,
        0,
        armH - padT * 0.5,
      ]),
    ]);
    const armX = seatW * 0.5 + postT;
    children.push({
      roleId: "armrest_l",
      name: "Armrest L",
      objectKind: "imported",
      geometry: armGeo,
      localTransform: lt([-armX, 0, seatH]),
    });
    children.push({
      roleId: "armrest_r",
      name: "Armrest R",
      objectKind: "imported",
      geometry: armGeo,
      localTransform: lt([armX, 0, seatH]),
    });
  }

  return {
    kind: "group",
    parent: { name: "Office Chair" },
    children,
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const OFFICE_CHAIR_GENERATOR: Generator =
  defineGenerator<OfficeChairParams>({
    id: "office_chair",
    label: "Office Chair",
    category: "prop",
    paramSpecs: OFFICE_CHAIR_SPECS,
    defaultParams: paramsFromSpecs(OFFICE_CHAIR_SPECS) as OfficeChairParams,
    build: buildOfficeChair,
  });
